import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Gamepad2, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { SponsoredCard } from "../components/SponsoredCard";
import { AffiliateCard } from "../components/AffiliateCard";
import { SponsorFeaturePanel } from "../components/SponsorFeaturePanel";
import { FeaturedCarousel } from "../components/FeaturedCarousel";
import { InkWeather } from "../components/InkWeather";
import { InkMarkets } from "../components/InkMarkets";
import { CuratedTechShorts } from "../components/CuratedTechShorts";
import { useT } from "../lib/i18n";

export default function Home() {
  const { t } = useT();
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [inkwire, setInkwire] = useState([]);
  const [creators, setCreators] = useState([]);
  const [ads, setAds] = useState([]);
  const [homeAff, setHomeAff] = useState(null);
  const [homeSideAff, setHomeSideAff] = useState(null);
  const [homeSponsor, setHomeSponsor] = useState(null);
  const [homeSponsorCard, setHomeSponsorCard] = useState(null);
  const [carousel, setCarousel] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/articles?limit=30").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/articles?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/homepage/carousel").then((r) => setCarousel(r.data)).catch(() => {});
    api.get("/inkwire").then((r) => setInkwire(r.data.slice(0, 5))).catch(() => {});
    api.get("/creators").then((r) => setCreators(r.data.slice(0, 3))).catch(() => {});
    api.get("/ads/active?placement=homepage").then((r) => setAds(r.data)).catch(() => {});
    api.get("/affiliate?placement=homepage_after_6&rotate=true").then((r) => setHomeAff(r.data[0] || null)).catch(() => {});
    api.get("/affiliate?placement=homepage_latest_sidebar&rotate=true").then((r) => setHomeSideAff(r.data[0] || null)).catch(() => {});
    api.get("/affiliate?placement=homepage_sponsor&rotate=true&ab=true").then((r) => setHomeSponsor(r.data[0] || null)).catch(() => {});
    api.get("/affiliate?placement=homepage_sponsor_card&rotate=true&ab=true").then((r) => setHomeSponsorCard(r.data[0] || null)).catch(() => {});
  }, []);

  const lead = featured[0] || articles[0];
  const secondary = (featured.slice(1, 4).length ? featured.slice(1, 4) : articles.slice(1, 4));
  const latest = articles.slice(0, 8);

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("You're on the list!"); setEmail(""); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const catSections = ["Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Lifestyle & Culture"];

  return (
    <div className="pti-container py-8" data-testid="home-page">
      {/* Featured carousel + Latest sidebar */}
      {(carousel.length > 0 || lead) && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 pti-fade-up">
          <div className="lg:col-span-8">
            <FeaturedCarousel items={carousel.length ? carousel : (lead ? [lead] : [])} />
          </div>
          <aside className="lg:col-span-4 flex flex-col divide-y divide-gray-200">
            <div className="text-[11px] uppercase tracking-widest font-bold text-gray-900 pb-3">{t("latest")}</div>
            {secondary.map((a) => (
              <div key={a.id} className="py-4 first:pt-0">
                <Link to={`/article/${a.slug}`} className="group block">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-pti-red">{a.category}</span>
                  <h3 className="font-display font-bold text-lg leading-tight mt-1 group-hover:text-pti-red transition-colors">{a.title}</h3>
                </Link>
              </div>
            ))}
            {/* One rotating ad in this column (rotates 50/50 across refreshes, e.g. Verizon Home Internet ↔ NordVPN). */}
            {homeSideAff && (
              <div className="pt-5" data-testid="homepage-sidebar-affiliate">
                <AffiliateCard ad={homeSideAff} placement="homepage_latest_sidebar" bypassOnePerPage className="shadow-md border-t-4 border-t-pti-red px-4 py-4 text-center" />
              </div>
            )}
          </aside>
        </section>
      )}

      {/* Latest grid — Row 1: 4 cards · Row 2: 2 cards · Row 3: rotating ad · Row 4: 2 cards */}
      <section className="mt-16">
        <SectionHeader title={t("sectionLatest")} to="/articles" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {latest.slice(0, 4).map((a) => <ArticleCard key={a.id} article={a} size="compact" />)}
        </div>
        {latest.length > 4 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
            {latest.slice(4, 6).map((a) => <ArticleCard key={a.id} article={a} size="compact" />)}
          </div>
        )}
        {/* Full-width rotating advertisement banner with reserved height to prevent layout jump */}
        {homeAff && (
          <div className="mt-10 w-full flex items-center justify-center min-h-[320px]" data-testid="home-ad-row">
            <div className="w-full max-w-3xl"><AffiliateCard ad={homeAff} placement="homepage_after_6" bypassOnePerPage /></div>
          </div>
        )}
        {latest.length > 6 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 ${homeAff ? "mt-10" : "mt-6"}`}>
            {latest.slice(6, 8).map((a) => <ArticleCard key={a.id} article={a} size="compact" />)}
          </div>
        )}
      </section>

      {/* Homepage sponsor — both units controlled from Admin → Affiliate Campaign Manager
          (placements: homepage_sponsor / homepage_sponsor_card), with self-serve ads as fallback. */}
      {(homeSponsor || homeSponsorCard || ads[0]) && (
        <section className="mt-16 grid lg:grid-cols-3 gap-6" data-testid="home-sponsor-section">
          <SponsorFeaturePanel
            ad={homeSponsor || ads[0] || null}
            source={homeSponsor ? "affiliate" : "active"}
            placement="homepage_sponsor" />
          {homeSponsorCard
            ? <AffiliateCard ad={homeSponsorCard} placement="homepage_sponsor_card" bypassOnePerPage />
            : (ads[1] || ads[0])
              ? <SponsoredCard ad={ads[1] || ads[0]} label="Sponsored" />
              : <div />}
        </section>
      )}

      {/* Curated Tech Shorts */}
      <CuratedTechShorts />

      {/* Newsroom tools */}
      <section className="mt-16">
        <div className="flex items-end justify-between border-b-2 border-pti-black pb-2">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">{t("newsroom")}</h2>
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("liveTools")}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <InkWeather />
          <InkMarkets />
        </div>
      </section>

      {/* InkWire preview */}
      <section className="mt-16 bg-pti-black text-white p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Radio className="text-pti-red" /> {t("inkwireFeed")}</h2>
          <Link to="/inkwire" className="text-sm font-bold text-pti-red hover:underline">{t("viewAll")} →</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-4">
          {inkwire.map((it) => (
            <a key={it.id} href={it.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 py-2 border-b border-neutral-800 group">
              <span className="text-[10px] uppercase tracking-wider font-bold text-pti-red mt-1 shrink-0 w-16">{it.category}</span>
              <span className="text-sm group-hover:text-pti-red transition-colors">{it.title}</span>
            </a>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-5">Curated by PictureThisInk · Headlines link to the original publisher.</p>
      </section>

      {/* Category sections */}
      {catSections.map((cat) => {
        const items = articles.filter((a) => a.category === cat).slice(0, 4);
        if (items.length === 0) return null;
        return (
          <section key={cat} className="mt-16">
            <SectionHeader title={cat} to={`/articles?category=${encodeURIComponent(cat)}`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-6">
              {items.map((a) => <ArticleCard key={a.id} article={a} size="compact" />)}
            </div>
          </section>
        );
      })}

      {/* Featured creators */}
      <section className="mt-16">
        <SectionHeader title={t("featuredCreators")} to="/creators" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {creators.map((c) => (
            <Link key={c.id} to={`/creators/${c.username}`} data-testid={`home-creator-${c.username}`} className="border border-gray-200 p-6 hover:border-gray-900 transition-colors flex items-center gap-4">
              <img src={c.avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200"} alt="" className="w-16 h-16 rounded-full object-cover bg-gray-100" />
              <div>
                <div className="font-bold flex items-center gap-1">{c.display_name}{c.verified && <CheckCircle2 size={14} className="text-pti-red" aria-label="Verified" />}</div>
                <div className="text-sm text-gray-500">@{c.username}</div>
                <div className="text-xs text-gray-400 mt-1">{c.followers} followers · {c.article_count} articles</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Arcade promo */}
      <section className="mt-16 bg-pti-black text-white p-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="font-display text-3xl font-bold flex items-center gap-2"><Gamepad2 className="text-pti-red" /> PictureThisInk Arcade</h2>
          <p className="text-neutral-400 mt-2 max-w-md">Take a break with premium free games — a Hextris-style puzzle, tower defense, and a space shooter. No ads, no downloads.</p>
        </div>
        <Link to="/pti-play" className="bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors shrink-0">{t("playNow")}</Link>
      </section>

      {/* Newsletter + advertise */}
      <section className="mt-16 grid md:grid-cols-2 gap-6">
        <form onSubmit={subscribe} data-testid="newsletter-form" className="border border-gray-200 p-8">
          <h3 className="font-display text-2xl font-bold">{t("newsletterTitle")}</h3>
          <p className="text-gray-600 mt-1 text-sm">Original stories, delivered weekly. No noise.</p>
          <div className="flex gap-2 mt-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("emailPlaceholder")}
              data-testid="newsletter-email" className="flex-1 border border-gray-300 px-4 py-2.5 focus:border-pti-red outline-none" aria-label="Email address" />
            <button type="submit" data-testid="newsletter-submit" className="bg-pti-black text-white px-5 py-2.5 font-bold hover:bg-pti-red transition-colors">{t("subscribe")}</button>
          </div>
        </form>
        <div className="bg-pti-red text-white p-8 flex flex-col justify-center">
          <h3 className="font-display text-2xl font-bold">{t("advertiseTitle")}</h3>
          <p className="mt-1 text-sm text-white/90">Build a beautiful native ad in minutes. Category-matched, brand-safe, and human.</p>
          <Link to="/advertise" data-testid="home-advertise-cta" className="inline-block mt-4 bg-white text-pti-red px-5 py-2.5 font-bold w-fit hover:bg-gray-100 transition-colors">{t("startCampaign")}</Link>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, to }) {
  const { t } = useT();
  return (
    <div className="flex items-end justify-between border-b-2 border-pti-black pb-2">
      <h2 className="font-display text-2xl sm:text-3xl font-bold">{title}</h2>
      <Link to={to} className="text-sm font-bold text-pti-red hover:underline shrink-0">{t("viewAll")} →</Link>
    </div>
  );
}
